// src/components/AccountBadge.js
//
// "What am I signed in as?" — answered in the dashboard profile card.
//
// It used to print `userData.role` raw, which meant a brand-new account had
// the word **unpaid** stamped on it in a purple pill. That is a database
// placeholder, not a status: it reads as an accusation, it stays there
// through a paid trial, and it tells the person nothing about what to do.
//
// Two pills instead:
//
//   🧑‍🏫 Educator          — what kind of account this is
//   Trial · bills 13 Sep   — where the money stands
//
// The account-type pill comes from utils/accountRole.js, so it is correct
// from the moment someone registers rather than waiting for the webhook to
// promote them.

import React from "react";
import { Link } from "react-router-dom";
import useAccessLevel, { ACCESS } from "../hooks/useAccessLevel";
import { roleDisplay, scopeLabel } from "../utils/accountRole";

function formatDate(value) {
  if (!value) return null;
  const d = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * The plan pill: text, colour, and whether it should link somewhere.
 */
function planPill({ level, hasPaidSubscription, subscriptionStatus, subscription, profile }) {
  if (level === ACCESS.ADMIN) {
    return { text: "Admin access", tone: "admin" };
  }

  if (hasPaidSubscription) {
    if (subscriptionStatus === "seat") {
      return { text: "Seat · full access", tone: "paid" };
    }

    if (subscriptionStatus === "trialing") {
      const renews = formatDate(
        subscription?.currentPeriodEnd ||
          subscription?.trialEnd ||
          profile?.subscription?.currentPeriodEnd
      );
      return {
        text: renews ? `Subscribed · bills ${renews}` : "Subscribed · in trial",
        tone: "paid",
      };
    }

    return { text: "Subscribed", tone: "paid" };
  }

  // Not paid. The local signup trial still grants full access, so say so
  // rather than calling them free.
  const trialEnd = formatDate(profile?.trial?.endDate);
  if (profile?.trial?.status === "active" && trialEnd) {
    return { text: `Free trial · ends ${trialEnd}`, tone: "trial", to: "/choose-plan" };
  }

  if (level === ACCESS.FREE) {
    return { text: "Free plan", tone: "free", to: "/choose-plan" };
  }

  return null;
}

export default function AccountBadge({ profile: profileProp = null, showScope = true, layout = "column" }) {
  const {
    level,
    profile: liveProfile,
    subscription,
    hasPaidSubscription,
    subscriptionStatus,
    isAdmin,
    loading,
  } = useAccessLevel();

  // The live profile is authoritative; the prop is a fallback for pages that
  // already hold the document and render before the listener reports.
  const profile = liveProfile || profileProp || {};

  const { icon, label } = roleDisplay(profile, { isAdmin });
  const scope = showScope ? scopeLabel(profile) : "";

  const plan = loading
    ? null
    : planPill({
        level,
        hasPaidSubscription,
        subscriptionStatus,
        subscription,
        profile,
      });

  return (
    <div style={layout === "row" ? { ...s.wrap, flexDirection: "row", flexWrap: "wrap", alignItems: "center", justifyContent: "flex-end" } : s.wrap}>
      <span style={{ ...s.pill, ...s.role }}>
        <span aria-hidden="true">{icon}</span>
        <span>{label}</span>
      </span>

      {plan &&
        (plan.to ? (
          <Link to={plan.to} style={{ ...s.pill, ...s[plan.tone], ...s.link }}>
            {plan.text}
          </Link>
        ) : (
          <span style={{ ...s.pill, ...s[plan.tone] }}>{plan.text}</span>
        ))}

      {scope && <span style={s.scope}>{scope}</span>}
    </div>
  );
}

const s = {
  wrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 6,
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 14px",
    borderRadius: 999,
    fontSize: "clamp(0.7rem, 1.6vw, 0.78rem)",
    fontWeight: 800,
    letterSpacing: "0.03em",
    whiteSpace: "nowrap",
  },
  link: { textDecoration: "none" },
  role: {
    color: "#fff",
    background: "linear-gradient(135deg, #e100ff, #7f00ff)",
    boxShadow: "0 6px 14px -8px rgba(127, 0, 255, 0.8)",
  },
  paid: {
    color: "#065f46",
    background: "#d1fae5",
    border: "1px solid #6ee7b7",
  },
  trial: {
    color: "#92400e",
    background: "#fef3c7",
    border: "1px solid #fcd34d",
  },
  free: {
    color: "#3b0764",
    background: "#f3e8ff",
    border: "1px solid #d8b4fe",
  },
  admin: {
    color: "#1e3a8a",
    background: "#dbeafe",
    border: "1px solid #93c5fd",
  },
  scope: {
    fontSize: 12,
    color: "#6b6280",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
};
